import { useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { keys } from '../../api/keys'
import { emit } from '../../platform/analytics/analytics'
import { useToast } from '../../platform/feedback/ToastProvider'
import type { PickExtras, PickerMerchant } from '../merchants/picker'
import { runBulk } from '../transactions/runBulk'
import type { Txn } from '../transactions/transactionWire'
import { failureOf, mayHaveCommitted, requestPatch, type SaveFailure } from '../transactions/useAutosaveField'
import { getTransaction } from '../transactions/useTransaction'

/** As bulk's: four PATCHes in flight at most. */
const CONCURRENCY = 4

const transactions = (n: number) => `${n} ${n === 1 ? 'transaction' : 'transactions'}`

/**
 * Set ONE merchant on several rows at once — the No merchant page's "Set all N at once?".
 *
 * There is no bulk endpoint, so this is one `PATCH {merchant_id}` per row, four at a time, as bulk
 * "Set merchant" does (`useBulkActions`); one row failing never stops the rest. Both caches are
 * invalidated once, after the whole set: the scoped list (the rows leave it) and every cashflow report
 * (the "No merchant" row and the merchant ranking move).
 *
 * One toast for the set, with ONE Undo that PATCHes each row that changed back to the merchant it held
 * when the user acted.
 *
 * **A failure is only certain when the server refused (4xx).** A 5xx or a lost response may have
 * committed, as `useQuickMerchant` knows: each such row is re-read (four at a time), and one that holds
 * the new merchant WAS changed — counted as set, and undone with the rest. One whose re-read shows the
 * old merchant was not changed. One whose re-read fails too is unconfirmed: it is neither counted as
 * changed nor undone, and the toast says so rather than claiming it unchanged (reviewer I2).
 *
 * **A brand added with "Also set category"** (`extras`) sets each row's category in the same PATCH, and
 * Undo puts back the category each row held as well as its merchant.
 *
 * Analytics: one `bulk_action_performed` with `action_kind: 'set_merchant'` and the counts — never the
 * merchant, the description or an id (rule 7). `error_count` is the rows not confirmed set.
 */
export function useMerchantForRows() {
  const client = useQueryClient()
  const toast = useToast()

  const refresh = useCallback(
    () =>
      Promise.all([
        client.invalidateQueries({ queryKey: keys.transactions.all() }),
        client.invalidateQueries({ queryKey: keys.cashflow.all() }),
      ]),
    [client],
  )

  /** Each PATCH's failure kind is kept by row id: runBulk keeps only a message. */
  const patchAll = useCallback(async (targets: readonly { id: string; merchantId: string | null; category?: { id: string | null } }[]) => {
    const kinds = new Map<string, SaveFailure>()
    const outcome = await runBulk(
      targets,
      (t) => t.id,
      async (t) => {
        try {
          await requestPatch(t.id, t.category === undefined ? { merchant_id: t.merchantId } : { merchant_id: t.merchantId, category_id: t.category.id })
        } catch (thrown) {
          kinds.set(t.id, failureOf(thrown))
          throw thrown
        }
      },
      { concurrency: CONCURRENCY, onProgress: () => {} },
    )
    return { ...outcome, kinds }
  }, [])

  /**
   * The failed rows, settled against the server: a 4xx is `unchanged` as it stands; a 5xx or lost
   * response is re-read, and lands in `changed` if the row now holds `merchantId`.
   */
  const settle = useCallback(async (failed: readonly string[], kinds: ReadonlyMap<string, SaveFailure>, merchantId: string) => {
    const changed: string[] = []
    const unchanged: string[] = []
    const uncertain = failed.filter((id) => {
      const kind = kinds.get(id)
      return kind !== undefined && mayHaveCommitted(kind)
    })
    unchanged.push(...failed.filter((id) => !uncertain.includes(id)))
    const reread = await runBulk(
      uncertain,
      (id) => id,
      async (id) => {
        const held = await getTransaction(id)
        ;(held.merchantId === merchantId ? changed : unchanged).push(id)
      },
      { concurrency: CONCURRENCY, onProgress: () => {} },
    )
    return { changed, unchanged, unconfirmed: reread.failed.map((f) => f.id) }
  }, [])

  const set = useCallback(
    async (rows: readonly Txn[], next: PickerMerchant, extras?: PickExtras) => {
      const before = new Map(rows.map((r) => [r.id, r] as const))
      const category = extras === undefined ? undefined : { id: extras.categoryId }
      const first = await patchAll(rows.map((r) => ({ id: r.id, merchantId: next.id, category })))
      const { changed, unchanged, unconfirmed } = await settle(
        first.failed.map((f) => f.id),
        first.kinds,
        next.id,
      )
      const done = [...first.done, ...changed]
      emit('bulk_action_performed', { action_kind: 'set_merchant', item_count: rows.length, error_count: rows.length - done.length })
      await refresh()
      if (done.length === 0) {
        toast.show({
          kind: 'error',
          message: unconfirmed.length > 0 ? 'Couldn’t confirm the merchant change. The list has been refreshed.' : 'Couldn’t set the merchant. Try again.',
        })
        return
      }
      const message = [
        done.length === rows.length
          ? `${transactions(done.length)} set to ${next.name}`
          : `${done.length} of ${transactions(rows.length)} set to ${next.name}.`,
        unchanged.length > 0 ? `${unchanged.length} couldn’t be changed.` : null,
        unconfirmed.length > 0 ? `${unconfirmed.length} couldn’t be confirmed.` : null,
      ]
        .filter((part) => part !== null)
        .join(' ')
      toast.show({
        kind: 'success',
        message,
        action: {
          label: 'Undo',
          onAct: () => {
            void (async () => {
              const undone = await patchAll(
                done.map((id) => {
                  const held = before.get(id)
                  return { id, merchantId: held?.merchantId ?? null, category: category === undefined ? undefined : { id: held?.categoryId ?? null } }
                }),
              )
              await refresh()
              if (undone.failed.length > 0) {
                toast.show({ kind: 'error', message: `Couldn’t undo. ${transactions(undone.failed.length)} still ${undone.failed.length === 1 ? 'has' : 'have'} the merchant.` })
              }
            })()
          },
        },
      })
    },
    [patchAll, settle, refresh, toast],
  )

  return { set }
}
